import { Check, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ClinicalNavBadgeTone = 'success' | 'warning' | 'destructive' | 'info' | 'muted';

export interface ClinicalNavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  count?: number;
  done?: boolean;
  required?: boolean;
  badge?: { label: string; tone: ClinicalNavBadgeTone };
}

export interface ClinicalNavGroup {
  id: string;
  label: string;
  items: readonly ClinicalNavItem[];
}

export interface ClinicalNavLabels {
  menu: string;
  done: string;
  required: string;
}

const BADGE_TONE: Record<ClinicalNavBadgeTone, string> = {
  success: 'bg-success/15 text-success',
  warning: 'bg-warning/15 text-warning',
  destructive: 'bg-destructive/15 text-destructive',
  info: 'bg-info/15 text-info',
  muted: 'bg-muted text-muted-foreground',
};

function Marker({ item, active, labels }: { item: ClinicalNavItem; active: boolean; labels: ClinicalNavLabels }) {
  if (item.badge) {
    return <span className={cn('ms-auto shrink-0 rounded-full px-1.5 text-[10px] font-semibold', BADGE_TONE[item.badge.tone])}>{item.badge.label}</span>;
  }
  if (item.count !== undefined && item.count > 0) {
    return (
      <span
        className={cn(
          'ms-auto min-w-[1.25rem] rounded-full px-1.5 text-center text-[11px] font-semibold tabular',
          active ? 'bg-primary text-primary-foreground' : item.required ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground',
        )}
      >
        {item.count}
      </span>
    );
  }
  if (item.done) {
    return (
      <span className="ms-auto grid h-4 w-4 place-items-center rounded-full bg-success/15 text-success" title={labels.done}>
        <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
        <span className="sr-only">{labels.done}</span>
      </span>
    );
  }
  if (item.required) {
    return (
      <span className="ms-auto inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold text-destructive">
        <span className="h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden />
        {labels.required}
      </span>
    );
  }
  return null;
}

function CompactMarker({ item, labels }: { item: ClinicalNavItem; labels: ClinicalNavLabels }) {
  if (item.badge) return <span className="opacity-80">{item.badge.label}</span>;
  if (item.count !== undefined && item.count > 0) return <span className="tabular opacity-80">{item.count}</span>;
  if (item.done) return <Check className="h-3 w-3 text-success" strokeWidth={3} aria-label={labels.done} />;
  if (item.required) return <span className="h-1.5 w-1.5 rounded-full bg-destructive" role="img" aria-label={labels.required} />;
  return null;
}

export function ClinicalNav({
  groups,
  active,
  onSelect,
  labels,
}: {
  groups: readonly ClinicalNavGroup[];
  active: string;
  onSelect: (id: string) => void;
  labels: ClinicalNavLabels;
}) {
  const visible = groups.filter((group) => group.items.length > 0);
  return (
    <>
      <nav
        aria-label={labels.menu}
        className="hidden overflow-y-auto rounded-2xl border bg-card p-2 shadow-sm lg:sticky lg:top-[calc(var(--emr-bar-top,0px)+var(--emr-bar-h,0px))] lg:block lg:max-h-[max(12rem,calc(var(--emr-scroll-h,100dvh)-var(--emr-bar-h,0px)-0.5rem))]"
      >
        {visible.map((group) => (
          <div key={group.id} className="mb-1 last:mb-0">
            <p className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">{group.label}</p>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const selected = item.id === active;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      aria-current={selected ? 'page' : undefined}
                      onClick={() => onSelect(item.id)}
                      className={cn(
                        'group relative flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-start text-sm transition-colors focus-ring',
                        selected ? 'bg-primary/10 font-medium text-primary' : 'text-foreground/80 hover:bg-muted hover:text-foreground',
                      )}
                    >
                      {selected && <span className="absolute inset-y-1.5 start-0 w-[3px] rounded-full bg-primary" aria-hidden />}
                      <span
                        className={cn(
                          'grid h-7 w-7 shrink-0 place-items-center rounded-md transition-colors',
                          selected ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/30' : 'bg-muted/70 text-muted-foreground group-hover:text-foreground',
                        )}
                        aria-hidden
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <span className="min-w-0 flex-1 truncate">{item.label}</span>
                      <Marker item={item} active={selected} labels={labels} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <nav aria-label={labels.menu} className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 lg:hidden">
        {visible.flatMap((group) =>
          group.items.map((item) => {
            const Icon = item.icon;
            const selected = item.id === active;
            return (
              <button
                key={item.id}
                type="button"
                aria-current={selected ? 'page' : undefined}
                onClick={() => onSelect(item.id)}
                className={cn(
                  'flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors focus-ring',
                  selected ? 'border-primary bg-primary text-primary-foreground' : 'bg-card text-foreground/80 hover:bg-muted',
                )}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {item.label}
                <CompactMarker item={item} labels={labels} />
              </button>
            );
          }),
        )}
      </nav>
    </>
  );
}
